import clsx from 'clsx';
import history from '@history';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import { Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ComposedBreadcrumb } from '../ComposedBreadcrumb/ComposedBreadcrumb';

/**
 * ComposedHeader: Componente para renderizar un encabezado compuesto con soporte para decoradores, títulos, descripciones y contenido adicional.
 *
 * @param {Object} props - Props del componente.
 * @param {React.ReactNode} [props.helper] - Contenido opcional que se mostrará como ayuda debajo del encabezado principal.
 * @param {string} props.title - Título principal del encabezado.
 * @param {string} [props.description] - Descripción secundaria debajo del título.
 * @param {React.ReactNode} [props.children] - Contenido adicional que se colocará en la sección derecha del encabezado.
 * @param {Function | string} [props.redirect] - Acción de redirección. Puede ser una función o una URL. Si no se proporciona, se usará `history.back()`.
 * @param {boolean} [props.disableRoute=false] - Si es `true`, deshabilita la navegación de las rutas del decorador `breadcrumb`.
 * @param {'breadcrumb' | 'button'} [props.decorator] - Decorador opcional que se mostrará en la esquina superior izquierda. Puede ser un "breadcrumb" o un botón de "Regresar".
 * @param {string} [props.className] - Clases CSS adicionales para personalizar el contenedor principal.
 * @param {Object} [props.props] - Otros props que se propagarán al contenedor principal.
 *
 * @returns {JSX.Element} Componente JSX para el encabezado compuesto.
 */
export const ComposedHeader = ({
  helper,
  title,
  description,
  children,
  redirect,
  disableRoute = false,
  decorator,
  className,
  ...props
}) => {
  return (
    <div
      className={clsx(
        'flex flex-col flex-1 w-full items-center justify-center space-y-8 sm:space-y-0 py-2 px-24 md:px-32',
        className
      )}
      {...props}
    >
      <div className="flex flex-col sm:flex-row flex-1 w-full items-center sm:items-end justify-between">
        <div className="flex flex-col items-center sm:items-start space-y-8 sm:space-y-0 md:w-1/3 sm:max-w-full min-w-0">
          {typeof decorator === 'string' && (
            <motion.div
              className="flex flex-row items-center mb-8 sm:items-start min-w-0 mx-8 sm:mr-14 sm:ml-10"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1, transition: { delay: 0.3 } }}
            >
              {decorator.toLowerCase() === 'breadcrumb' && (
                <ComposedBreadcrumb disableRoute={disableRoute} redirect={redirect} />
              )}
              {decorator.toLowerCase() === 'button' && (
                <Button
                  onClick={typeof redirect !== 'function' ? () => history.back() : redirect}
                  color="primary"
                  variant="text"
                  size="large"
                  startIcon={<ArrowBackIcon />}
                >
                  Regresar
                </Button>
              )}
            </motion.div>
          )}

          <div className="flex items-center max-w-full">
            <motion.div
              className="flex flex-col items-center sm:items-start min-w-0 mx-8 sm:mx-16"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, transition: { delay: 0.3 }, opacity: 1 }}
            >
              <Typography className="text-16 sm:text-24 truncate font-semibold">{title}</Typography>
              <Typography variant="caption" className="font-medium">
                {description}
              </Typography>
            </motion.div>
          </div>
        </div>
        <motion.div
          className="md:w-2/3 sm:max-w-full min-w-0 h-full flex flex-auto flex-row"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0, transition: { delay: 0.3 } }}
        >
          <div className="w-full flex flex-wrap flex-row justify-center sm:justify-end">
            {children}
          </div>
        </motion.div>
      </div>
      {helper && (
        <motion.div
          className="flex w-full flex-col pl-14"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { delay: 0.3 } }}
        >
          {helper}
        </motion.div>
      )}
    </div>
  );
};
